/** Image upload/replace/clear widget for a single image field (cover image,
 *  meeting-point photo). Controlled tri-state:
 *   - `null`      → keep the current image (no change)
 *   - `File`      → replace with the staged file (sent as a multipart part)
 *   - `'cleared'` → delete the current image (sent as an explicit null)
 *
 * PROJECT RULE — never load a full-size original inline. Pass a backend image
 * `{p,s}` ref via `imageRef`: the inline preview is then a DOWNSIZED thumbnail
 * (resize service) and clicking it opens the ORIGINAL in a new tab. Only sources
 * without a resizable ref (a logo, a PDF attachment, a route-map) should use the
 * plain `currentUrl`/`fullUrl` string props.
 */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Chip, FormLabel, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { imageUrl, IMAGE_PRESET, type ImageChunks } from '@/utils/imageUrl';

export type ImageFieldValue = File | 'cleared' | null;

export function ImageUploadField({
  label, currentUrl, imageRef, value, onChange, canManage, accept = 'image/*', fullUrl,
}: {
  label: string;
  /** Pre-built preview URL — only for sources WITHOUT a resizable `{p,s}` ref
   *  (a tour-operator logo, a PDF attachment, a route-map). For a backend image,
   *  prefer `imageRef`, which downsizes automatically per the project rule. */
  currentUrl?: string | null;
  /** Backend image `{p,s}` ref. When set, the inline preview is a DOWNSIZED
   *  thumbnail and clicking it opens the ORIGINAL in a new tab. Takes precedence
   *  over `currentUrl`/`fullUrl`. */
  imageRef?: ImageChunks | { p: string | null; s: string | null } | null;
  value: ImageFieldValue;
  onChange: (v: ImageFieldValue) => void;
  canManage: boolean;
  accept?: string;
  /** When set (and no `imageRef`), the current (non-staged) image links to this
   *  URL — clicking opens the full image in a new tab. Opt-in. */
  fullUrl?: string | null;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
    return undefined;
  }, [value]);

  // The rule in one place: a resizable ref → a downsized inline preview + the
  // original behind a click; a url-only source → the caller's currentUrl/fullUrl.
  const ref = imageRef && imageRef.p && imageRef.s ? { p: imageRef.p, s: imageRef.s } : null;
  const resolvedCurrent = ref ? imageUrl(ref, IMAGE_PRESET.DETAIL) : (currentUrl ?? null);
  const resolvedFull = ref ? imageUrl(ref) : (fullUrl ?? null);

  const shownUrl = useMemo(() => {
    if (value instanceof File) return preview;
    if (value === 'cleared') return null;
    return resolvedCurrent;
  }, [value, preview, resolvedCurrent]);

  // Only the current (non-staged) image links out, and only when a full URL is known.
  const openUrl = value instanceof File ? null : resolvedFull;

  const thumbSx = {
    width: 96, height: 64, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider',
  } as const;

  return (
    <Box>
      <FormLabel sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5, display: 'block' }}>{label}</FormLabel>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {shownUrl ? (
          openUrl ? (
            <Box
              component="a" href={openUrl} target="_blank" rel="noopener noreferrer"
              title="Open full image in a new tab" sx={{ display: 'inline-flex', lineHeight: 0 }}
            >
              <Box component="img" src={shownUrl} alt={label} sx={{ ...thumbSx, cursor: 'pointer', display: 'block' }} />
            </Box>
          ) : (
            <Box component="img" src={shownUrl} alt={label} sx={thumbSx} />
          )
        ) : (
          <Box sx={{
            width: 96, height: 64, borderRadius: 1, border: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled',
          }}>
            <Typography variant="caption">None</Typography>
          </Box>
        )}

        <Stack spacing={0.5}>
          <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={!canManage}>
            {value instanceof File || resolvedCurrent ? 'Replace' : 'Upload'}
            <input hidden type="file" accept={accept}
              onChange={(e) => { const f = e.target.files?.[0] ?? null; if (f) onChange(f); e.target.value = ''; }} />
          </Button>
          {value instanceof File && (
            <Chip size="small" color="info" label={`Staged: ${value.name}`}
              onDelete={canManage ? () => onChange(null) : undefined} />
          )}
          {value === 'cleared' && (
            <Button size="small" color="inherit" startIcon={<UndoIcon />} disabled={!canManage}
              onClick={() => onChange(null)}>
              Undo remove
            </Button>
          )}
          {value === null && resolvedCurrent && (
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={!canManage}
              onClick={() => onChange('cleared')}>
              Remove
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
